/**
 * Symmetry – Custom JS (vanilla, no jQuery, no cart logic)
 * - Ei jQuery-riippuvuuksia
 * - Ei ostoskoriin liittyviä skriptejä
 * - Id:t, classit ja data-attribuutit ennallaan
 */

/* ----------------------------------------------------------
 *  Mini-utils (kevyt korvike jQuerylle)
 * ---------------------------------------------------------- */
const $$ = (sel, root=document) => Array.from(root.querySelectorAll(sel));
const $1 = (sel, root=document) => root.querySelector(sel);

const dom = {
  on(el, ev, fn, opts) { el && el.addEventListener(ev, fn, opts); },
  text(el, v) { if (!el) return; if (v===undefined) return el.textContent; el.textContent = v; },
  html(el, v) { if (!el) return; if (v===undefined) return el.innerHTML; el.innerHTML = v; },
  val(el, v) { if (!el) return; if (v===undefined) return el.value; el.value = v; },
  show(el) { if (el) el.style.display = ''; },
  hide(el) { if (el) el.style.display = 'none'; },
  attr(el, k, v) { if (!el) return; if (v===undefined) return el.getAttribute(k); el.setAttribute(k, v); },
  rmAttr(el, k) { if (el) el.removeAttribute(k); },
  prop(el, k, v) { if (!el) return; el[k] = v; },
  addCls(el, ...c) { if (el) el.classList.add(...c); },
  rmCls(el, ...c) { if (el) el.classList.remove(...c); },
  hasCls(el, c) { return !!el && el.classList.contains(c); },
  trigger(el, type, opts={bubbles:true}) { if (!el) return; el.dispatchEvent(new Event(type, opts)); },
  closest(el, sel){ return el ? el.closest(sel) : null; },
};

/* ----------------------------------------------------------
 *  i18n (DE & SV lisätty kovakoodattuihin teksteihin)
 * ---------------------------------------------------------- */
const LANG = (document.documentElement.lang || '').toLowerCase();
const _langKey = () =>
  LANG.startsWith('de') ? 'de' :
  LANG.startsWith('sv') ? 'sv' :
  LANG.startsWith('en') ? 'en' : 'fi';

const I18N = {
  fi: {
    length_over_max: 'Pituudet yli {max} mm eivät ole saatavilla',
    pack_abbr: 'pkt',
    running_m_abbr: 'jm',
    delivery_time: 'Toimitusaika',
    not_selected: 'Ei valittu',
    week: 'Viikko',
    sample_btn: 'Tilaa mallipala',
    modal_link: 'Mittausohje',
    modal_close_aria: 'kiinni',
    modal_title: 'Laudelauta mittausohje:',
    bullets: [
      'Ota tilasta mitat aina paneelista paneeliin (tehdas huomioi tarvittavan asennusvaran)',
      'Syötä saunan leveys -kenttään tilan tarkka leveys millimetreinä mm',
      'Kiuasseinän leveys millimetreinä mm',
      'Tilan syvyys millimetreinä mm',
      'Teemme sinulle juuri oikean kokoiset lauteet'
    ],
    notice_max_width: 'Lauteiden leveys voi olla maksimissaan 2200 mm.',
    notice_width_zero: 'Leveys ei voi olla 0 mm, ilmoita saunan leveys tilataksesi laudepaketin.',
    attr_labels: ['Materiaali:', 'Käsittely:', 'Laatu:']
  },
  de: {
    length_over_max: 'Längen über {max} mm sind nicht verfügbar',
    pack_abbr: 'Pk.',
    running_m_abbr: 'lfm',
    delivery_time: 'Lieferzeit',
    not_selected: 'Nicht ausgewählt',
    week: 'Woche',
    sample_btn: 'Kostenloses Muster bestellen',
    modal_link: 'Messanleitung',
    modal_close_aria: 'schließen',
    modal_title: 'Messanleitung für Sitzbank-Bretter:',
    bullets: [
      'Messen Sie den Raum immer von Paneel zu Paneel (die Fabrik berücksichtigt die notwendige Montagefuge).',
      'Geben Sie in das Feld „Saunabreite“ die exakte Breite des Raums in Millimetern ein.',
      'Breite der Ofenwand in Millimetern.',
      'Tiefe des Raums in Millimetern.',
      'Wir fertigen die Bänke genau in Ihrer Größe.'
    ],
    notice_max_width: 'Die Bankbreite darf maximal 2200 mm betragen.',
    notice_width_zero: 'Die Breite darf nicht 0 mm sein. Bitte geben Sie die Saunabreite an, um das Bankpaket zu bestellen.',
    attr_labels: ['Material:', 'Behandlung:', 'Qualität:']
  },
  sv: {
    length_over_max: 'Längder över {max} mm är inte tillgängliga',
    pack_abbr: 'pkt',
    running_m_abbr: 'lm',
    delivery_time: 'Leveranstid',
    not_selected: 'Inte vald',
    week: 'Vecka',
    sample_btn: 'Beställ provbit',
    modal_link: 'Mätanvisning',
    modal_close_aria: 'stäng',
    modal_title: 'Mätanvisning för lavar:',
    bullets: [
      'Mät alltid utrymmet från panel till panel (fabriken beaktar erforderlig monteringsmån).',
      'Ange exakt bredd i fältet ”Bastu bredd” i millimeter.',
      'Bredd på vägg med bastuaggregat i millimeter.',
      'Utrymmets djup i millimeter.',
      'Vi tillverkar lavar exakt enligt dina mått.'
    ],
    notice_max_width: 'Lavarnas bredd får vara högst 2200 mm.',
    notice_width_zero: 'Bredden kan inte vara 0 mm. Ange bastuns bredd för att beställa lavpaket.',
    attr_labels: ['Material:', 'Behandling:', 'Kvalitet:']
  },
  en: {
    length_over_max: 'Lengths over {max} mm are not available',
    pack_abbr: 'pk',
    running_m_abbr: 'lm',
    delivery_time: 'Delivery time',
    not_selected: 'Not selected',
    week: 'Week',
    sample_btn: 'Order sample',
    modal_link: 'Measuring guide',
    modal_close_aria: 'close',
    modal_title: 'Bench board measuring guide:',
    bullets: [
      'Measure the space from panel to panel (factory accounts for installation allowance).',
      'Enter exact room width in millimeters in the width field.',
      'Heater wall width in millimeters.',
      'Room depth in millimeters.',
      'We make benches exactly to your size.'
    ],
    notice_max_width: 'Bench width may be max 2200 mm.',
    notice_width_zero: 'Width cannot be 0 mm. Enter sauna width to order the bench package.',
    attr_labels: ['Material:', 'Treatment:', 'Quality:']
  }
};
const tr = (key) => I18N[_langKey()][key];
const trFmt = (key, vars={}) => (tr(key) || '').replace(/\{(\w+)\}/g, (_,k)=> (vars[k] ?? ''));

/* ----------------------------------------------------------
 *  Apu: hinnan parsiminen ja näkyvän hinnan nouto
 * ---------------------------------------------------------- */
function parsePrice (str) {
  return parseFloat(String(str).replace(/[^0-9,\.]/g, '').replace(',', '.'));
}
function getCurrentPrice () {
  const sale = $1('.price--on-sale .price__current .js-value');
  if (sale) return parsePrice(sale.textContent);
  const regular = $1('.product-info__price .price__current .js-value');
  if (regular) return parsePrice(regular.textContent);
  return NaN;
}

/* ----------------------------------------------------------
 *  PÄÄSKRIPTI
 * ---------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  const lang = (document.documentElement.lang || '').toLowerCase();

  /* --------------------------------------------
   * DE + SV: Estä yli 3900 mm Pituus-vaihtoehdot
   * -------------------------------------------- */
  (function limitLengthForLanguages(){
    if (!/^(de|sv|sv-se)$/.test(lang)) return;
    const MAX_LEN = 3900;
    const fs = $1('fieldset.option-selector[data-option="Pituus"]');
    if (!fs) return;

    $$('.option-selector__btns .opt-label', fs).forEach(label => {
      const input = label.previousElementSibling && label.previousElementSibling.matches('input[type=radio]')
        ? label.previousElementSibling : null;
      const raw = ($1('.js-value', label)?.textContent || '').trim();
      const mm = parseFloat(String(raw).replace(/[^0-9,\.]/g, '').replace(',', '.'));

      if (input && mm > MAX_LEN) {
        input.disabled = true;
        dom.addCls(label, 'disabledradio', 'is-disabled');
        dom.attr(label, 'aria-disabled', 'true');
        dom.attr(label, 'tabindex', '-1');
        dom.attr(label, 'title', trFmt('length_over_max', {max: MAX_LEN}));
      }
    });

    const checked = $1('input[type=radio]:checked', fs);
    if (checked && checked.disabled) {
      const fallback = $$('input[type=radio]', fs).find(r => {
        if (r.disabled) return false;
        const txt = ($1('.js-value', r.nextElementSibling)?.textContent || '').trim();
        const mm = parseFloat(String(txt).replace(/[^0-9,\.]/g, '').replace(',', '.'));
        return mm <= MAX_LEN;
      });
      if (fallback) {
        fallback.checked = true;
        dom.trigger(fallback, 'change');
        dom.trigger(fallback, 'click');
      } else {
        $$('.product__submit__buttons button[type=submit], .product__submit__buttons .btn')
          .forEach(b => { b.disabled = true; dom.addCls(b, 'is-disabled'); });
      }
    }
  })();

  /* --------------------------------------------
   * Menekkilaskuri (#menekki-kontin sisällä)
   * -------------------------------------------- */
  (function menekki() {
    const men = $1('#menekki');
    if (!men) return;

    const kerroin = parseFloat(men.dataset.kerroin || '');
    if (!kerroin) { dom.hide(men); return; }

    // aseta data-jm valitun variantin ja paketin koon mukaan
    function updateJMFromVariant() {
      const kpl = parseFloat(men.dataset.kpl || '0'); // paketin kappalemäärä
      const jsVal = $1('.option-selector__btns input[type=radio]:checked+label .js-value');
      const mm = jsVal ? parseFloat((jsVal.textContent || '').replace(/[^\d,.-]/g, '').replace(',', '.')) : NaN;
      if (!isNaN(mm) && kpl > 0) dom.attr(men, 'data-jm', (kpl * mm) / 1000); // jm/pack
    }
    updateJMFromVariant();

    // syöte: pinta-ala → pituus, hukka ja paketit
    dom.on(men, 'input', (evt) => {
      if (!(evt.target && evt.target.matches('#product-area'))) return;

      const area = parseFloat(evt.target.value || '0');
      const i = $1('#product-length');
      const l = $1('#product-length-visib');
      const e = $1('#product-spare');
      const f = $1('#product-spare-visib');
      const packs = $1('#product-packs');
      const jm = parseFloat(men.dataset.jm || '0'); // jm/pack

      const len = Math.round(area * kerroin * 100 + Number.EPSILON) / 100;
      if (i) dom.val(i, len);
      if (l) dom.text(l, `${len} m`);

      const spare = Math.round(1.1 * len); // 10% hukka mukaan
      if (e) dom.text(e, `${spare} m`);
      if (f) dom.text(f, `${spare} m`);

      // PYÖRISTÄ YLÖSPÄIN → kokonaispaketit
      const pkInt = jm ? Math.ceil(spare / jm) : 0;
      const packAbbr = tr('pack_abbr');
      if (packs) dom.text(packs, `${pkInt} ${packAbbr}`);
    });

    // Kun pituusvaihtoehtoa klikataan
    $$('.opt-label').forEach(label => {
      dom.on(label, 'click', () => {
        setTimeout(() => {
          const area = parseFloat(($1('#product-area')?.value) || '0');
          const i = $1('#product-length');
          const l = $1('#product-length-visib');
          const e = $1('#product-spare');
          const f = $1('#product-spare-visib');
          const packs = $1('#product-packs');

          const checkedMM = parseFloat(($1('.option-selector__btns input[type=radio]:checked+label .js-value')?.textContent || '').replace(/[^\d,.-]/g, '').replace(',', '.'));
          const kpl = parseFloat(men.dataset.kpl || '0'); // käytä aina data-kpl
          const jm = (!isNaN(checkedMM) && kpl > 0) ? (checkedMM * kpl) / 1000 : 0; // jm/pack
          dom.attr(men, 'data-jm', jm);

          const len = Math.round(area * kerroin * 100 + Number.EPSILON) / 100;
          if (i) dom.val(i, len);
          if (l) dom.text(l, `${len} m`);

          const spare = Math.round(1.1 * len);
          if (e) dom.text(e, `${spare} m`);
          if (f) dom.text(f, `${spare} m`);

          // PYÖRISTÄ YLÖSPÄIN → kokonaispaketit
          const pkInt = jm ? Math.ceil(spare / jm) : 0;
          const packAbbr = tr('pack_abbr');
          if (packs) dom.text(packs, `${pkInt} ${packAbbr}`);

          // alennusbadgejen 5 % stepit
          $$('.product__price--off span').forEach(span => {
            const n = parseFloat((span.textContent || '0').replace('%',''));
            const m = Math.ceil(n / 5) * 5;
            dom.text(span, `${m}%`);
          });
        }, 200);
      });
    });

    // Reagoi teeman varianttitapahtumaan
    document.addEventListener('on:variant:change', updateJMFromVariant);
  })();

  /* --------------------------------------------
   * Neliötuote / metrihinnoittelu -kortit
   * -------------------------------------------- */
  (function pricingBlocks(){
    const neliotuote = dom.attr($1('.countNelio'), 'data-neliotuote');
    const variantLength = $$('.option-selector__btns').length;
    if (!neliotuote) {
      $$('.countElem').forEach(dom.hide);
      $$('.product-block--sales-point').forEach(el => dom.addCls(el, 'noCount'));
    }

    const priceAttr = dom.attr($1('.countPrice'), 'data-price');
    const price = (priceAttr === '') 
      ? parseFloat(dom.attr($1('.countAle'), 'data-ale') || '0')/100
      : parseFloat(priceAttr || '0')/100;

    const ale = parseFloat(dom.attr($1('.countAle'), 'data-ale') || '0')/100;
    const leveys = parseFloat(dom.attr($1('.countLeveys'), 'data-hyotyleveys') || '0');
    const pituus = parseFloat(dom.attr($1('.countPituus'), 'data-pituus') || '0');
    const paketti = parseFloat(dom.attr($1('.countPaketti'), 'data-pakettikoko') || '0');
    const men = $1('#menekki');

    const deliveryLabels = [
      I18N.fi.delivery_time, I18N.de.delivery_time, I18N.sv.delivery_time, I18N.en.delivery_time
    ];
    const showToimitusaikaLineOnly = () => {
      $$('.product-block--sales-point').forEach(el => {
        const txt = (el.textContent || '').trim();
        if (deliveryLabels.some(l => l && txt.includes(l))) dom.show(el);
        else dom.hide(el);
      });
      $$('.turhaviiva').forEach(dom.hide);
    };

    function setMyyntiera(totalPituus, pakettim2, kplhinta){
      dom.text($1('.myyntiera .paketti'), String(paketti));
      dom.text($1('.myyntiera .kplhinta'), kplhinta.toFixed(2));
      const tp = $1('.myyntiera .totalPituus');
      if (tp) {
        dom.text(tp, totalPituus.toFixed(2));
        tp.insertAdjacentText('beforeend', tr('running_m_abbr'));
      }
      const tn = $1('.myyntiera .totalNeliot');
      if (tn && !isNaN(pakettim2)) {
        dom.text(tn, pakettim2.toFixed(2));
        tn.insertAdjacentText('afterbegin', ' = ');
        tn.insertAdjacentText('beforeend', 'm²');
      }
      $$('.countResults').forEach(dom.show);
    }

    function setMetrihintaAle(val){
      const slot = $1('.metrihinta .ale');
      if (slot) dom.text(slot, val.toFixed(2));
    }
    function setNeliohintaAle(val){
      const slot = $1('.neliohinta .ale');
      if (slot) dom.text(slot, val.toFixed(2));
    }

    if (neliotuote === 'true') {
      showToimitusaikaLineOnly();

      if (variantLength > 0) {
        const ale2 = getCurrentPrice();
        const sel = $1('.option-selector__btns input[type=radio]:checked+label');
        const pit = parseFloat((sel?.textContent || '').replace(' mm','')) || pituus;

        const neliomm = leveys * pit;
        const neliom2 = (neliomm/1000)/1000;
        const pakettim2 = paketti * neliom2;
        const metrit = paketti * pit;
        const totalPituus = metrit/1000;
        const kplhinta = ale2 / paketti;
        const neliohintaAle = ale2 / pakettim2;
        const metrihintaAle = ale2 / (metrit/1000);

        setMetrihintaAle(metrihintaAle);
        setNeliohintaAle(neliohintaAle);
        setMyyntiera(totalPituus, pakettim2, kplhinta);
        if (men) dom.attr(men, 'data-jm', totalPituus.toFixed(2));
      } else {
        const neliomm = leveys * pituus;
        const neliom2 = (neliomm/1000)/1000;
        const pakettim2 = paketti * neliom2;
        const metrit = paketti * pituus;
        const totalPituus = metrit/1000;
        const neliohintaAle = ale / pakettim2;
        const metrihintaAle = ale / (metrit/1000);
        const kplhinta = ale / paketti;

        setMetrihintaAle(metrihintaAle);
        setNeliohintaAle(neliohintaAle);
        setMyyntiera(totalPituus, pakettim2, kplhinta);
        if (men) dom.attr(men, 'data-jm', totalPituus.toFixed(2));
      }

      // päivitys kun pituus valitaan
      $$('.opt-label').forEach(label => {
        dom.on(label, 'click', () => {
          const pit = parseFloat((label.textContent || '').replace(' mm',''));
          const neliomm = leveys * pit;
          const neliom2 = (neliomm/1000)/1000;
          const pakettim2 = paketti * neliom2;
          const metrit = paketti * pit;
          const totalPituus = metrit/1000;

          setTimeout(() => {
            const ale2 = getCurrentPrice();
            const kplhinta = ale2 / paketti;
            setMyyntiera(totalPituus, pakettim2, kplhinta);
            if (men) dom.attr(men, 'data-jm', totalPituus.toFixed(2));
          }, 500);
        });
      });

    } else {
      // ei-neliötuote
      $$('.product-block--sales-point').forEach(dom.hide);
      showToimitusaikaLineOnly();
      $$('.neliohinta').forEach(dom.hide);
      $$('.turhaviiva').forEach(dom.hide);

      if (variantLength > 0) {
        const ale2 = getCurrentPrice();
        const sel = $1('.option-selector__btns input[type=radio]:checked+label');
        const pit = parseFloat((sel?.textContent || '').replace(' mm','')) || pituus;
        const metrit = paketti * pit;
        const totalPituus = metrit/1000;
        const metrihintaAle = ale2 / (metrit/1000);
        const kplhinta = ale2 / paketti;

        setMetrihintaAle(metrihintaAle);
        setMyyntiera(totalPituus, NaN, kplhinta);
        if (men) dom.attr(men, 'data-jm', totalPituus.toFixed(2));
      } else {
        const metrit = paketti * pituus;
        const totalPituus = metrit/1000;
        const metrihintaAle = ale / (metrit/1000);
        const kplhinta = ale / paketti;

        setMetrihintaAle(metrihintaAle);
        setMyyntiera(totalPituus, NaN, kplhinta);
        if (men) dom.attr(men, 'data-jm', totalPituus.toFixed(2));
      }

      $$('.opt-label').forEach(label => {
        dom.on(label, 'click', () => {
          const pit = parseFloat((label.textContent || '').replace(' mm',''));
          const metrit = paketti * pit;
          const totalPituus = metrit/1000;

          setTimeout(() => {
            const ale2 = getCurrentPrice();
            const kplhinta = ale2 / paketti;
            setMyyntiera(totalPituus, NaN, kplhinta);
            const men = $1('#menekki');
            if (men) dom.attr(men, 'data-jm', totalPituus.toFixed(2));
          }, 500);
        });
      });
    }
  })();

  /* --------------------------------------------
   * Toimitusaika – valintakomponentin generointi (ei kori-integraatiota)
   * -------------------------------------------- */
  (function toimitusaika(){
    // lisää .toimitusaika-luokka samaan kohtaan kuin ennen
    const taBlock = $1('.product__block.variant__countdown+.product__custom-code+.product__block.product__block--lines');
    if (taBlock) dom.addCls(taBlock, 'toimitusaika');

    const host = $1('.toimitusaika');
    if (!host) return;

    const toivottuText = LANG.startsWith('de') ? 'Erwartete' : (LANG.startsWith('sv') ? 'Önskad' : (LANG.startsWith('en') ? 'Expected' : 'Toivottu'));
    const weekText = tr('week');
    const notSelected = tr('not_selected');

    const serial = Array.from({length:53}, (_,i)=> (i+1).toString().padStart(2,'0'));
    const list = serial.map((val,i)=>
      `<li class="select-popout__item n${i}" data-week="${weekText} ${val}">
        <a class="select-popout__option" href="#" data-value="${weekText} ${val}" data-popout-option="">
          <span>${weekText} ${val}</span>
        </a>
      </li>`
    ).join('');

    host.insertAdjacentHTML('beforeend',
      `<popout-select class="select-popout">
        <button type="button" class="select-popout__toggle" aria-expanded="false" data-popout-toggle="">
          <span data-popout-toggle-text="">${notSelected}</span>
          <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-nav-arrow-down" viewBox="0 0 24 24">
            <path d="m6 9 6 6 6-6" stroke="#000" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </button>
        <ul class="select-popout__list toimitusList" data-popout-list="" data-scroll-lock-scrollable="" style="--max-width:635px;">
          <li class="select-popout__item is-active"><a class="select-popout__option" href="#" aria-current="true" data-value="${notSelected}" data-popout-option=""><span>${notSelected}</span></a></li>
          ${list}
        </ul>
        <input type="hidden" class="selectedToimitusaika" value="${notSelected}" data-popout-input="" data-single-option-selector="" data-index="option1">
      </popout-select>`
    );

    // napin arvon siirto properties-inputtiin
    dom.on($1('.toimitusaika .select-popout'), 'click', () => {
      const active = $1('.select-popout__item.is-active');
      const txt = active ? (active.textContent || '').trim() : '';
      const propInput = $1(".toimitusaika input.properties__input");
      if (propInput) { propInput.value = txt; propInput.textContent = txt; }
    });

    // piilota natiivi input name*="toivottuText"
    $$(`.toimitusaika input[name*="${toivottuText}"]`).forEach(dom.hide);

    // esivalitse kuluva viikko (kieli-offsetit kuten aiemmassa logiikassa)
    const today = new Date();
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
    const pastDaysOfYear = (today - firstDayOfYear) / 86400000;
    const dayNum = today.getDay();
    let curWeek;
    if ([3,4,5,6].includes(dayNum)) {
      curWeek = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7) + (LANG.startsWith('de') ? 2 : 1);
    } else {
      curWeek = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7) + (LANG.startsWith('de') ? 1 : 0);
    }
    const cur = (curWeek < 10) ? `${weekText} 0${curWeek}` : `${weekText} ${curWeek}`;
    const inp = $1('input.selectedToimitusaika');
    const toggleTxt = $1('.toimitusaika .select-popout__toggle span');
    if (inp) inp.value = cur;
    if (toggleTxt) dom.text(toggleTxt, cur);
    $$('.toimitusaika .select-popout__item.is-active').forEach(el=>dom.rmCls(el, 'is-active'));
    const act = $$('.toimitusaika .select-popout__item').find(li => (li.textContent||'').includes(cur));
    if (act) dom.addCls(act, 'is-active');

    const hidden = $1(`.toimitusaika input[name*="${toivottuText}"]`);
    if (hidden) { hidden.value = cur; hidden.textContent = cur; }
  })();

  /* --------------------------------------------
   * Sample-painike kokoelma-avaimen perusteella
   * -------------------------------------------- */
  (function sampleButton(){
    const prodbtn = dom.attr($1("div[data-section-type='product']"), 'data-collectionnew') || '';
    const wrap = $1('.product__submit__buttons');
    if (!wrap) return;

    const appendBtn = (href, text) => wrap.insertAdjacentHTML('beforeend',
      `<div><a href='${href}' class='btn btn--secondary btn--solid' style='display:block;'>${text}</a></div>`);

    if (LANG.startsWith('fi')) {
      if (prodbtn.includes('sauna'))        appendBtn('/products/ilmainen-mallipala-saunatuotteet', tr('sample_btn'));
      else if (prodbtn.includes('lista'))   appendBtn('/products/ilmainen-mallipala-sisustuslistat', tr('sample_btn'));
      else if (prodbtn.includes('terassi')) appendBtn('/products/ilmainen-mallipala-terassilaudat', tr('sample_btn'));
      else if (prodbtn.includes('ulko'))    appendBtn('/products/ilmainen-mallipala-ulkoverhouspaneelit', tr('sample_btn'));
      else if (prodbtn.includes('sisustus'))appendBtn('/products/ilmainen-mallipala-sisustuspaneelit', tr('sample_btn'));
    } else if (LANG.startsWith('de')) {
      if (prodbtn.includes('sauna'))        appendBtn('/products/kostenloses-muster-saunaprodukte', tr('sample_btn'));
      else if (prodbtn.includes('lista'))   appendBtn('/products/kostenloses-muster-innenleisten', tr('sample_btn'));
      else if (prodbtn.includes('terassi')) appendBtn('/products/kostenloses-muster-terrassendielen', tr('sample_btn'));
      else if (prodbtn.includes('ulko'))    appendBtn('/products/kostenloses-muster-aussenverkleidungspaneele', tr('sample_btn'));
      else if (prodbtn.includes('sisustus'))appendBtn('/products/kostenloses-muster-wand-und-deckenpaneele', tr('sample_btn'));
    } else if (LANG.startsWith('sv')) {
      // Jos erillisiä ruotsinkielisiä tuotesivuja ei ole, käytetään FI-URLeja.
      if (prodbtn.includes('sauna'))        appendBtn('/products/ilmainen-mallipala-saunatuotteet', tr('sample_btn'));
      else if (prodbtn.includes('lista'))   appendBtn('/products/ilmainen-mallipala-sisustuslistat', tr('sample_btn'));
      else if (prodbtn.includes('terassi')) appendBtn('/products/ilmainen-mallipala-terassilaudat', tr('sample_btn'));
      else if (prodbtn.includes('ulko'))    appendBtn('/products/ilmainen-mallipala-ulkoverhouspaneelit', tr('sample_btn'));
      else if (prodbtn.includes('sisustus'))appendBtn('/products/ilmainen-mallipala-sisustuspaneelit', tr('sample_btn'));
    } else {
      // EN fallback → käytä FI-URLeja mutta käännetty teksti
      if (prodbtn.includes('sauna'))        appendBtn('/products/ilmainen-mallipala-saunatuotteet', tr('sample_btn'));
      else if (prodbtn.includes('lista'))   appendBtn('/products/ilmainen-mallipala-sisustuslistat', tr('sample_btn'));
      else if (prodbtn.includes('terassi')) appendBtn('/products/ilmainen-mallipala-terassilaudat', tr('sample_btn'));
      else if (prodbtn.includes('ulko'))    appendBtn('/products/ilmainen-mallipala-ulkoverhouspaneelit', tr('sample_btn'));
      else if (prodbtn.includes('sisustus'))appendBtn('/products/ilmainen-mallipala-sisustuspaneelit', tr('sample_btn'));
    }
  })();


  /* --------------------------------------------
   * Piilota tyhjät inforivit tuotesivulla
   * -------------------------------------------- */
  (function hideEmptyInfoRows(){
    if (!window.location.pathname.includes('/products/')) return;
    const labels = I18N[_langKey()].attr_labels || [];
    $$('.lightly-spaced-row .with-icon__beside').forEach(el => {
      const text = (el.textContent || '').trim();
      const isHit = labels.includes(text);
      if (isHit) {
        const wrapper = dom.closest(el, '.lightly-spaced-row');
        if (wrapper) dom.hide(wrapper);
      }
    });
  })();
});

/* ---------- Cart coverage (m², 10 % hukka huomioitu) ---------- */
(function () {
  const $  = (s, r=document) => Array.from(r.querySelectorAll(s));
  const $1 = (s, r=document) => r.querySelector(s);
  const num = s => parseFloat(String(s||'').replace(/[^\d,.-]/g,'').replace(',', '.'));

  function labelPack(lang) {
    lang = (lang || '').toLowerCase();
    if (lang.startsWith('sv')) return { title: 'Den här mängden räcker till', note: '(inkl. 10 % spill)' };
    if (lang.startsWith('de')) return { title: 'Mit dieser Menge',            note: '(inkl. 10% Verschnitt)' };
    if (lang.startsWith('en')) return { title: 'This quantity yields',        note: '(incl. 10% waste)' };
    return { title: 'Tällä määrällä valmista', note: '(sis. 10% hukka)' }; // FI default
  }

  function computeItem(item) {
    // Älä lisää duplikaatteja
    if (item.querySelector('.cart-coverage')) return;

    // Hyötyleveys (mm) on PAKOLLINEN → jos puuttuu, ei näytetä mitään
    const hyRaw = item.getAttribute('data-hyotyleveys-mm');
    if (!hyRaw) return;
    const hyMm = num(hyRaw);
    if (!hyMm || hyMm <= 0) return;

    // Yksikköhinnan pitää olla per m
    const unitEl = $1('.unit-price__unit', item);
    if (!unitEl) return;
    const unit = (unitEl.textContent || '').trim();
    if (unit !== 'm') return;

    // € / m ja € / kpl (paketti) + määrä
    const unitPricePerM = num($1('.unit-price__price', item)?.textContent);
    const pricePerPack  = num($1('.cart-item__selling-price', item)?.textContent);
    const qty           = num($1('.cart-item__quantity-input', item)?.value) || 1;
    if (!unitPricePerM || !pricePerPack) return;

    // Juoksumetrit yhteensä = (kpl-hinta * määrä) / (€/m)
    const metresTotal = (pricePerPack * qty) / unitPricePerM;

    // m² = jm * hyötyleveys(m)
    const area = metresTotal * (hyMm / 1000);

    // 10 % hukka huomioituna: “mitä tällä määrällä voi kattaa” = nimellisala / 1.10
    const areaAfterWaste = area / 1.10;

    // Lisää näkyviin (tuotteen tietojen alle)
    const host = $1('.cart-item__description .small-text', item) || $1('.cart-item__description', item);
    if (!host) return;

    const t = labelPack((document.documentElement.lang||'').toLowerCase());
    const row = document.createElement('div');
    row.className = 'cart-coverage';
    row.innerHTML = `
      <span class="cart-coverage__label">${t.title}:</span>
      <span class="cart-coverage__value">${areaAfterWaste.toFixed(2)} m²</span>
      <span class="cart-coverage__note">${t.note}</span>
    `;
    host.appendChild(row);
  }

  function run(root=document){ $('.cart-item', root).forEach(computeItem); }

  // Aja kun drawer avataan ja kun cart DOM mergeää (Symmetryn eventit)
  document.addEventListener('on:cart-drawer:after-open', () => run());
  document.addEventListener('on:cart:after-merge', () => run());

  // Ensiajo varmuuden vuoksi
  run();

  // Pysy perässä muutoksista drawerissa
  const list = document.querySelector('.cart-item-list__body');
  if (list) {
    const mo = new MutationObserver(() => run(list));
    mo.observe(list, {childList:true, subtree:true});
  }
})();

/* Move breadcrumbs above collection H1 in hero */
(function () {
  // run only on collection pages that have the hero structure
  function isCollectionHeroPresent() {
    return document.querySelector('.image-with-text-growly .feature-text-paired .majortitle');
  }

  function moveBreadcrumbs() {
    const title = document.querySelector('.image-with-text-growly .feature-text-paired .majortitle');
    const bc = document.querySelector('nav.ba-breadcrumb');
    if (!title || !bc) return;

    // prevent duplicate moves
    if (bc.dataset.moved === '1') return;

    // insert just before H1
    title.parentElement.insertBefore(bc, title);
    bc.dataset.moved = '1';
  }

  function init() {
    if (!isCollectionHeroPresent()) return;

    // Try immediately
    moveBreadcrumbs();

    // If app injects breadcrumbs later, observe DOM and move when available
    const mo = new MutationObserver(() => moveBreadcrumbs());
    mo.observe(document.documentElement, { childList: true, subtree: true });

    // Safety: stop observing once moved
    const stopIfMoved = setInterval(() => {
      const bc = document.querySelector('nav.ba-breadcrumb');
      if (bc && bc.dataset.moved === '1') {
        mo.disconnect();
        clearInterval(stopIfMoved);
      }
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

document.addEventListener("DOMContentLoaded", function() {
  const lang = (document.documentElement.lang || "").toLowerCase();

  // Tekstit jotka halutaan piilottaa kielikohtaisesti
  const hideTexts = {
    de: ["Kostenloser Versand"], // Saksa
    sv: ["Fri frakt på beställningar över 1000 euro"] // Ruotsi
  };

  Object.keys(hideTexts).forEach(locale => {
    if (lang.startsWith(locale)) {
      document.querySelectorAll(".announcement__text").forEach(el => {
        hideTexts[locale].forEach(txt => {
          if (el.textContent.includes(txt)) {
            const announcement = el.closest(".announcement");
            if (announcement) {
              announcement.style.display = "none"; // piilota vain tämä
            }
          }
        });
      });
    }
  });
});


document.addEventListener('DOMContentLoaded', function(){
  const lang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
  const isDE = lang.startsWith('de');
  const isSV = lang.startsWith('sv');  // ruotsi

  // Jos ei DE tai SV, ei tehdä mitään
  if (!isDE && !isSV) return;

  // Käytetään mapin avaimissa ilman loppukaksoispistettä -> kestävämpi eri teemoissa
  const MAP_DE = {
    'Saunan leveys (mm) *':        'Saunabreite (mm) *',
    'Kiuasseinän leveys (mm) *':   'Breite der Saunawand (mm) *',
    'Tilan syvyys (mm) *':         'Raumtiefe (mm) *'
  };
  const MAP_SV = {
    'Saunan leveys (mm) *':        'Bastubredd (mm) *',
    'Kiuasseinän leveys (mm) *':   'Bredd på bastuväggen (mm) *',
    'Tilan syvyys (mm) *':         'Rumsdjup (mm) *'
  };

  const MAP = isDE ? MAP_DE : MAP_SV;

  const stripColon = s => s.replace(/\s*:\s*$/, '');
  const withColon  = s => s.endsWith(':') ? s : (s + ':');

  function replacePropertyLabels() {
    document.querySelectorAll('.line-item-property__label').forEach(el => {
      const orig = el.textContent.trim();
      const key  = stripColon(orig);
      if (MAP[key]) el.textContent = withColon(MAP[key]);
    });
  }

  // (Valinnainen) Jos haluat kääntää/piilottaa myös varianttien otsikointia korissa:
  function tweakVariantLabels() {
    document.querySelectorAll('.cart-item__variant-label').forEach(el => {
      const t = el.textContent.trim();
      // Piilota kenttä “Valittu leveys / Ausgewählte Breite / Vald bredd”
      if (t === 'Valittu leveys:' || t === 'Ausgewählte Breite:' || t === 'Vald bredd:') {
        el.closest('.cart-item__variant')?.setAttribute('style','display:none;');
      }
      // (tarvittaessa voisi tehdä myös käännöksen: esim. SV 'Valittu leveys:' -> 'Vald bredd:')
      if (isSV && t === 'Valittu leveys:') el.textContent = 'Vald bredd:';
    });
  }

  const run = () => {
    replacePropertyLabels();
    tweakVariantLabels();
  };

  run();
  new MutationObserver(run).observe(document.body, { childList: true, subtree: true });
});


(function() {
  // Säädä kynnys tarvittaessa
  var MAX_LEN_MM = 3900;

  // Jos Liquid-muuttuja puuttuisi, yritetään lukea Shopify.country:iin tukeutuen.
  var country = (window.marketCountry || (window.Shopify && Shopify.country) || '').toUpperCase();

  function parseMm(text) {
    if (!text) return NaN;
    // Poimii kaikki numerot (myös 4 480 -tyyliset) ja muuttaa kokonaisluvuksi
    var digits = ('' + text).replace(/[^\d]/g, '');
    return digits ? parseInt(digits, 10) : NaN;
  }

  function hideLongRadios(root) {
    var containerList = (root || document).querySelectorAll('.option-selector__btns');
    containerList.forEach(function(container) {
      var radios = container.querySelectorAll('input[type="radio"].opt-btn, input[type="radio"].js-option');
      if (!radios.length) return;

      // Kerätään sallitut ja kielletyt
      var allowed = [];
      var blocked = [];

      radios.forEach(function(radio) {
        // Arvo voidaan lukea input.value:sta tai labelin .js-value-spanista
        var label = container.querySelector('label[for="' + radio.id + '"]');
        var labelText = label ? label.textContent.trim() : radio.value;
        var mm = parseMm(labelText || radio.value);

        var isTooLong = !isNaN(mm) && mm > MAX_LEN_MM;
        var shouldHide = (country !== 'FI') && isTooLong;

        if (shouldHide) {
          blocked.push({ radio: radio, label: label });
        } else {
          allowed.push({ radio: radio, label: label, mm: mm });
        }
      });

      // Piilotetaan kielletyt
      blocked.forEach(function(item) {
        if (item.label) item.label.style.display = 'none';
        item.radio.style.display = 'none';
        item.radio.disabled = true;
        // Jos piilotettu oli valittuna, poistetaan valinta
        if (item.radio.checked) item.radio.checked = false;
      });

      // Varmistetaan, että jokin sallittu on valittuna
      var anyChecked = allowed.some(function(a) { return a.radio.checked; });
      if (!anyChecked && allowed.length) {
        // Valitse lähin sallittu mitan perusteella (tai ensimmäinen)
        allowed.sort(function(a,b){
          // Pienin ero 3900:sta
          var da = isNaN(a.mm) ? Number.MAX_SAFE_INTEGER : Math.abs(a.mm - MAX_LEN_MM);
          var db = isNaN(b.mm) ? Number.MAX_SAFE_INTEGER : Math.abs(b.mm - MAX_LEN_MM);
          return da - db;
        });
        allowed[0].radio.checked = true;
        allowed[0].radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function hideLongSelects(root) {
    // Jos joissain malleissa käytetään <select>-pudotusvalikkoa
    var selects = (root || document).querySelectorAll('select[name*="option"], .single-option-selector');
    selects.forEach(function(sel) {
      var options = Array.from(sel.options || []);
      var hasChange = false;

      options.forEach(function(opt) {
        var mm = parseMm(opt.text || opt.value);
        var shouldHide = (country !== 'FI') && !isNaN(mm) && mm > MAX_LEN_MM;
        if (shouldHide) {
          // Disabloi ja merkitse piilo CSS:llä
          opt.disabled = true;
          opt.setAttribute('data-hidden-by-market', 'true');
          hasChange = true;
        } else {
          opt.disabled = false;
          opt.removeAttribute('data-hidden-by-market');
        }
      });

      if (hasChange) {
        // Jos nykyinen valinta on kielletty, vaihdetaan sallittuun
        if (sel.selectedOptions && sel.selectedOptions[0] && sel.selectedOptions[0].disabled) {
          var firstAllowed = options.find(function(o){ return !o.disabled; });
          if (firstAllowed) {
            sel.value = firstAllowed.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    });
  }

  function applyHiding(root) {
    // Suomessa ei piiloteta mitään
    if (country === 'FI') return;
    hideLongRadios(root);
    hideLongSelects(root);
  }

  // Aja kun DOM valmis
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function(){ applyHiding(document); });
  } else {
    applyHiding(document);
  }

  // Havaitse dynaamiset uudelleenrenderöinnit (teemat usein rakentavat optiot AJAXilla)
  var obs = new MutationObserver(function(muts) {
    var needs = muts.some(function(m){
      return Array.from(m.addedNodes || []).some(function(n){
        return n.nodeType === 1 && (
          n.matches && (n.matches('.option-selector__btns') || n.matches('select[name*="option"], .single-option-selector')) ||
          (n.querySelector && (n.querySelector('.option-selector__btns') || n.querySelector('select[name*="option"], .single-option-selector')))
        );
      });
    });
    if (needs) applyHiding(document);
  });
  obs.observe(document.documentElement, { childList: true, subtree: true });

  // Jos kaupassa on maa-/markkinavalitsin, yritetään reagoida sen vaihdokseen
  window.addEventListener('shopify:country:changed', function(e) {
    // Jos eventistä saa maan, käytä sitä; muuten luetaan uudelleen window.marketCountry
    country = (e && e.detail && e.detail.country) ? String(e.detail.country).toUpperCase()
             : (window.marketCountry || (window.Shopify && Shopify.country) || '').toUpperCase();
    // Nollaa mahdollinen aiempi piilotus ja aja uudestaan
    // (Näytetään kaiken varalta ensin kaikki)
    document.querySelectorAll('.option-selector__btns label.opt-label, .option-selector__btns input[type="radio"]').forEach(function(el){
      el.style.removeProperty('display');
      if (el.tagName === 'INPUT') el.disabled = false;
    });
    document.querySelectorAll('select[name*="option"] option[data-hidden-by-market]').forEach(function(opt){
      opt.disabled = false;
      opt.removeAttribute('data-hidden-by-market');
    });
    applyHiding(document);
  });

})();

  // Ruotsin kovakoodaus markkinavalikkoon
(() => {
  const TARGET_DOMAIN = "https://centbrandstire6.shop"; // Vaihda tarvittaessa

  function getCountrySelectRoot() {
    // Etsitään nimenomaan country-valitsin (button-id päättyy -country-button)
    const btn = document.getElementById("annbar-localization-country-button");
    if (!btn) return null;
    return btn.closest(".custom-select");
  }

  function ensureSwedenOption(root) {
    const list = root?.querySelector(".custom-select__listbox");
    if (!list) return;

    // Älä lisää duplikaatteja
    if (list.querySelector('[data-value="SE"]')) return;

    // Rakenne samaa muotoa kuin muissa vaihtoehdoissa
    const li = document.createElement("li");
    li.className = "custom-select__option flex items-center js-option";
    li.id = "country-selector-country-opt-se";
    li.setAttribute("role", "option");
    li.setAttribute("data-value", "SE");
    li.setAttribute("aria-selected", "false");
    li.innerHTML = `
      <span class="pointer-events-none">
        <span class="country-option">
          <span class="country-option__name">Ruotsi</span>
          <span class="country-option__currency">(SEK&nbsp;kr)</span>
        </span>
      </span>
    `;

    list.appendChild(li);
  }

  function bindRedirect(root) {
    if (!root || root.__seBound) return;
    root.__seBound = true;

    const handler = (e) => {
      const li = e.target.closest(".custom-select__option");
      if (li && li.getAttribute("data-value") === "SE") {
        e.preventDefault();
        e.stopPropagation();

        // Jos haluat säilyttää polun, ota nämä kaksi riviä käyttöön
        // const preservePath = location.pathname + location.search + location.hash;
        // window.location.href = TARGET_DOMAIN.replace(/\/$/, "") + preservePath;

        // Perusohjaus etusivulle:
        window.location.href = TARGET_DOMAIN;
      }
    };

    const keyHandler = (e) => {
      const active = document.activeElement?.closest(".custom-select__option");
      if (active && active.getAttribute("data-value") === "SE" && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = TARGET_DOMAIN;
      }
    };

    root.addEventListener("click", handler);
    root.addEventListener("keydown", keyHandler);
  }

  function init() {
    const root = getCountrySelectRoot();
    if (!root) return;
    ensureSwedenOption(root);
    bindRedirect(root);
  }

  // Ensimmäinen init
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Jos valitsin renderöityy uudelleen (AJAX/SPAs), pidetään SE-vaihtoehto mukana
  const mo = new MutationObserver(() => init());
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
